import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Download, FileSpreadsheet, Trash2, X } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { enumMasterService, projectService, taskService, userService } from '@/services/api';
import type { EnumMasterItem } from '@/services/api/enumMasterService';
import { QUERY_KEYS } from '@/services/queryClient';
import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { Milestone, Project, TaskPriority, User } from '@/types';
import { AppMenuItem, AppSelect } from '@/shared/components/AppSelect';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { downloadTaskImportTemplate, parseTaskImportFile, ParsedTaskRow } from '../utils/parseTaskImport';

interface DraftRow {
  key: string;
  title: string;
  categoryText: string;
  category: string;
  startDate: string;
  dueDate: string;
}

interface ImportTasksDialogProps {
  open: boolean;
  onClose: () => void;
}

function userDisplayName(user: User): string {
  return user.displayName || [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email;
}

function matchCategory(text: string, categories: EnumMasterItem[]): string {
  const normalized = text.trim().toLowerCase();
  if (!normalized) return '';
  const hit = categories.find(
    (item) =>
      item.displayLabel.trim().toLowerCase() === normalized ||
      item.keyValue.trim().toLowerCase() === normalized
  );
  return hit?.keyValue ?? '';
}

function toDraftRows(parsed: ParsedTaskRow[], categories: EnumMasterItem[]): DraftRow[] {
  return parsed.map((row, index) => ({
    key: `${Date.now()}-${index}`,
    title: row.title,
    categoryText: row.categoryText,
    category: matchCategory(row.categoryText, categories),
    startDate: row.startDate,
    dueDate: row.dueDate,
  }));
}

function rowError(row: DraftRow): string {
  if (!row.title.trim()) return 'Title is required';
  if (row.categoryText.trim() && !row.category) return `"${row.categoryText}" is not a category`;
  if (!row.startDate) return 'Start date is required';
  if (!row.dueDate) return 'Due date is required';
  if (row.startDate > row.dueDate) return 'Start date must be on or before the due date';
  return '';
}

const ImportTasksDialog: React.FC<ImportTasksDialogProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const { showSuccess } = useSnackbar();
  const currentUser = useAppSelector(selectCurrentUser);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [projectId, setProjectId] = useState('');
  const [milestoneId, setMilestoneId] = useState('');
  const [priority, setPriority] = useState<TaskPriority>(TaskPriority.P3);
  const [responsibleUserId, setResponsibleUserId] = useState('');
  const [accountableUserId, setAccountableUserId] = useState(currentUser?.id ?? '');
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [fileName, setFileName] = useState('');
  const [formError, setFormError] = useState('');

  const { data: taskCategories = [] } = useQuery({
    queryKey: QUERY_KEYS.taskCategories(),
    queryFn: () => enumMasterService.listTaskCategories(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const { data: projectsData } = useQuery({
    queryKey: [...QUERY_KEYS.projects, 'task-import'],
    queryFn: () => projectService.getProjects(undefined, 1, 100),
    enabled: open,
  });

  const { data: users = [] } = useQuery({
    queryKey: ['raci-assignable-users', projectId || 'all', 'task-import'],
    queryFn: () => userService.getRACIAssignableUsers(projectId || undefined),
    enabled: open,
  });

  useEffect(() => {
    if (!open || taskCategories.length === 0) return;
    setRows((current) =>
      current.map((row) => {
        if (row.category || !row.categoryText) return row;
        const category = matchCategory(row.categoryText, taskCategories);
        return category ? { ...row, category } : row;
      })
    );
  }, [open, taskCategories]);

  const projects = (projectsData?.items ?? []) as Project[];
  const milestones: Milestone[] = useMemo(() => {
    const project = projects.find((item) => item.id === projectId);
    return project?.milestones ?? [];
  }, [projects, projectId]);

  const rowErrors = useMemo(() => rows.map(rowError), [rows]);
  const hasRowErrors = rowErrors.some(Boolean);

  const importMutation = useMutation({
    mutationFn: () =>
      taskService.importTasks({
        projectId: projectId || null,
        milestoneId: milestoneId || null,
        priority,
        responsibleUserId,
        accountableUserId,
        tasks: rows.map((row) => ({
          title: row.title.trim(),
          category: row.category || undefined,
          startDate: row.startDate,
          dueDate: row.dueDate,
        })),
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tasks });
      showSuccess(`${result.created} task${result.created === 1 ? '' : 's'} imported`);
      handleClose();
    },
    onError: (error: { response?: { data?: { message?: string } }; message?: string }) => {
      setFormError(error?.response?.data?.message || error?.message || 'Import failed');
    },
  });

  const handleClose = () => {
    setRows([]);
    setFileName('');
    setFormError('');
    setProjectId('');
    setMilestoneId('');
    setPriority(TaskPriority.P3);
    setResponsibleUserId('');
    setAccountableUserId(currentUser?.id ?? '');
    onClose();
  };

  const handleFile = async (file: File) => {
    setFormError('');
    try {
      const parsed = await parseTaskImportFile(file);
      if (parsed.length === 0) {
        setFormError('No task rows found. Use columns Title, Category, Start Date, and Due Date.');
        setRows([]);
        setFileName('');
        return;
      }
      setRows(toDraftRows(parsed, taskCategories));
      setFileName(file.name);
    } catch (error) {
      setRows([]);
      setFileName('');
      setFormError(error instanceof Error ? error.message : 'Could not read that file');
    }
  };

  const updateRow = (key: string, patch: Partial<DraftRow>) => {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const canImport =
    rows.length > 0 &&
    !hasRowErrors &&
    Boolean(responsibleUserId) &&
    Boolean(accountableUserId) &&
    !importMutation.isPending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Import tasks
        <IconButton onClick={handleClose} size="small" aria-label="Close">
          <X size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each row is one task. Upload a CSV or Excel file. Title, category, start date, and due date come from the file.
          Project, milestone, priority, and people apply to every row. Dates can be changed on the task after import.
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 2 }}>
          <AppSelect
            label="Project (optional)"
            value={projectId}
            onChange={(event) => {
              setProjectId(String(event.target.value));
              setMilestoneId('');
            }}
          >
            <AppMenuItem value="">No project</AppMenuItem>
            {projects.map((project) => (
              <AppMenuItem key={project.id} value={project.id}>
                {project.name}
              </AppMenuItem>
            ))}
          </AppSelect>
          <AppSelect
            label="Milestone (optional)"
            value={milestoneId}
            disabled={!projectId || milestones.length === 0}
            onChange={(event) => setMilestoneId(String(event.target.value))}
          >
            <AppMenuItem value="">No milestone</AppMenuItem>
            {milestones.map((milestone) => (
              <AppMenuItem key={milestone.id} value={milestone.id}>
                {milestone.name}
              </AppMenuItem>
            ))}
          </AppSelect>
          <AppSelect
            label="Priority"
            value={priority}
            onChange={(event) => setPriority(event.target.value as TaskPriority)}
          >
            <AppMenuItem value={TaskPriority.P1}>P1 - Critical</AppMenuItem>
            <AppMenuItem value={TaskPriority.P2}>P2 - High</AppMenuItem>
            <AppMenuItem value={TaskPriority.P3}>P3 - Medium</AppMenuItem>
            <AppMenuItem value={TaskPriority.P4}>P4 - Low</AppMenuItem>
          </AppSelect>
          <AppSelect
            label="Responsible"
            value={responsibleUserId}
            onChange={(event) => setResponsibleUserId(String(event.target.value))}
          >
            <AppMenuItem value="">Select</AppMenuItem>
            {users.map((user) => (
              <AppMenuItem key={user.id} value={user.id}>
                {userDisplayName(user)}
              </AppMenuItem>
            ))}
          </AppSelect>
          <AppSelect
            label="Accountable"
            value={accountableUserId}
            onChange={(event) => setAccountableUserId(String(event.target.value))}
          >
            <AppMenuItem value="">Select</AppMenuItem>
            {users.map((user) => (
              <AppMenuItem key={user.id} value={user.id}>
                {userDisplayName(user)}
              </AppMenuItem>
            ))}
          </AppSelect>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Button variant="outlined" startIcon={<Download size={16} />} onClick={() => downloadTaskImportTemplate('csv')}>
            CSV template
          </Button>
          <Button variant="outlined" startIcon={<Download size={16} />} onClick={() => downloadTaskImportTemplate('xlsx')}>
            Excel template
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileSpreadsheet size={16} />}
            onClick={() => fileInputRef.current?.click()}
          >
            {fileName || 'Choose CSV or Excel'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            accept=".xlsx,.xls,.csv"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void handleFile(file);
            }}
          />
        </Box>

        {formError && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
            {formError}
          </Alert>
        )}

        {rows.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Category</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Start date</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Due date</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.key}>
                    <TableCell sx={{ minWidth: 280 }}>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.title}
                        error={Boolean(rowErrors[index])}
                        helperText={rowErrors[index] || ' '}
                        onChange={(event) => updateRow(row.key, { title: event.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <AppSelect
                        value={row.category}
                        error={Boolean(row.categoryText && !row.category)}
                        onChange={(event) =>
                          updateRow(row.key, {
                            category: String(event.target.value),
                            categoryText: '',
                          })
                        }
                      >
                        <AppMenuItem value="">No category</AppMenuItem>
                        {taskCategories.map((item) => (
                          <AppMenuItem key={item.id} value={item.keyValue}>
                            {item.displayLabel}
                          </AppMenuItem>
                        ))}
                      </AppSelect>
                    </TableCell>
                    <TableCell>
                      <DatePicker
                        value={row.startDate ? dayjs(row.startDate) : null}
                        onChange={(value: Dayjs | null) =>
                          updateRow(row.key, { startDate: value ? value.format('YYYY-MM-DD') : '' })
                        }
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </TableCell>
                    <TableCell>
                      <DatePicker
                        value={row.dueDate ? dayjs(row.dueDate) : null}
                        onChange={(value: Dayjs | null) =>
                          updateRow(row.key, { dueDate: value ? value.format('YYYY-MM-DD') : '' })
                        }
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        aria-label="Remove row"
                        onClick={() => setRows((current) => current.filter((item) => item.key !== row.key))}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canImport}
          onClick={() => {
            setFormError('');
            importMutation.mutate();
          }}
        >
          {importMutation.isPending
            ? 'Importing...'
            : rows.length
              ? `Import ${rows.length} task${rows.length === 1 ? '' : 's'}`
              : 'Import tasks'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportTasksDialog;
